// utilities
import { getRegionName } from '@/utilities/access/accessGroupings'
// types
import type { AccessDealership } from '@/utilities/access/accessGroupings'

/** Checkbox state of a tree node: empty, dash, or tick */
export type AccessCheckState = 'unchecked' | 'partial' | 'checked'

/** A single site leaf in the tree */
export interface AccessTreeDealership {
  id: number
  name: string
  manufacturerName: string | null
  type: string | null
}

/** An org branch holding the sites that belong to it */
export interface AccessTreeOrg {
  id: number
  name: string
  dealerships: AccessTreeDealership[]
}

/** A region branch holding org branches, plus any sites with no org */
export interface AccessTreeRegion {
  id: string
  name: string
  orgs: AccessTreeOrg[]
  /** Sites that carry no usable org ID, selectable individually only */
  orphans: AccessTreeDealership[]
}

/** The selection the tree edits, mirroring the `org` and `dealership` grant scopes */
export interface AccessTreeSelection {
  orgs: number[]
  dealerships: number[]
}

/**
 * Orgs the granter may hand out as a whole. `null` means every org, which is
 * only true of someone who holds all access themselves.
 */
export type ScopableOrgIds = number[] | null

/** Label used when a site has no region of its own */
const UNKNOWN_REGION_NAME = 'Unknown region'

/**
 * Check that an identifier is a usable numeric ID.
 *
 * @param value - The value to test
 * @returns True when the value is a finite number
 */
function isNumericId(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** Sort comparator ordering nodes alphabetically by name */
const byName = <T extends { name: string }>(a: T, b: T): number => a.name.localeCompare(b.name)

/**
 * Build a region > org > dealership tree from a flat dealership list.
 *
 * An org that spans regions appears under each of its regions holding only that
 * region's sites. Ticking either occurrence still grants the whole org, because
 * org grants are not region scoped.
 *
 * @param dealerships - Resolved dealership list from the permissions endpoint
 * @returns Region branches sorted by name, with orgs and sites sorted by name
 */
export function buildAccessTree(dealerships: AccessDealership[]): AccessTreeRegion[] {
  const regions = new Map<string, AccessTreeRegion>()

  for (const dealership of dealerships) {
    const regionId = (dealership.region ?? '').toLowerCase()

    let region = regions.get(regionId)
    // First site in the region creates the branch
    if (!region) {
      region = {
        id: regionId,
        name: regionId ? getRegionName(regionId) : UNKNOWN_REGION_NAME,
        orgs: [],
        orphans: []
      }
      regions.set(regionId, region)
    }

    const site: AccessTreeDealership = {
      id: dealership.id,
      name: dealership.name,
      manufacturerName: dealership.manufacturerName ?? null,
      type: dealership.type ?? null
    }

    // Without an org there is no scope to grant, so the site sits on the region
    if (!isNumericId(dealership.orgId)) {
      region.orphans.push(site)
      continue
    }

    let org = region.orgs.find(candidate => candidate.id === dealership.orgId)
    // First site in the org creates the branch
    if (!org) {
      org = {
        id: dealership.orgId,
        name: dealership.orgName ?? `Org ${dealership.orgId}`,
        dealerships: []
      }
      region.orgs.push(org)
    }
    org.dealerships.push(site)
  }

  return [...regions.values()]
    .map(region => ({
      ...region,
      orgs: region.orgs
        .map(org => ({ ...org, dealerships: [...org.dealerships].sort(byName) }))
        .sort(byName),
      orphans: [...region.orphans].sort(byName)
    }))
    .sort(byName)
}

/**
 * Count every site beneath a region branch.
 *
 * @param region - The region branch to count
 * @returns Total number of sites in the region
 */
export function countRegionDealerships(region: AccessTreeRegion): number {
  return region.orgs.reduce((total, org) => total + org.dealerships.length, 0) + region.orphans.length
}

/**
 * Count the sites of an org that are individually selected.
 *
 * @param org - The org branch to inspect
 * @param selection - The current selection
 * @returns Number of individually selected sites
 */
export function countSelectedOrgDealerships(org: AccessTreeOrg, selection: AccessTreeSelection): number {
  return org.dealerships.filter(site => selection.dealerships.includes(site.id)).length
}

/**
 * Whether the whole org is granted as a scope, rather than site by site.
 *
 * @param org - The org branch to inspect
 * @param selection - The current selection
 * @returns True when an org scope grant covers the org
 */
export function isOrgScoped(org: AccessTreeOrg, selection: AccessTreeSelection): boolean {
  return selection.orgs.includes(org.id)
}

/**
 * Resolve the checkbox state of an org branch.
 *
 * @param org - The org branch to inspect
 * @param selection - The current selection
 * @returns Tick for the whole org, dash for some of its sites, empty for none
 */
export function getOrgCheckState(org: AccessTreeOrg, selection: AccessTreeSelection): AccessCheckState {
  // An org scope covers every site in the org, including sites added later
  if (isOrgScoped(org, selection)) {
    return 'checked'
  }

  // An org with no sites can only ever be empty
  if (org.dealerships.length === 0) {
    return 'unchecked'
  }

  const selected = countSelectedOrgDealerships(org, selection)

  // No sites selected leaves the box empty
  if (selected === 0) {
    return 'unchecked'
  }

  return selected === org.dealerships.length ? 'checked' : 'partial'
}

/**
 * Resolve the checkbox state of a region branch from its children.
 *
 * @param region - The region branch to inspect
 * @param selection - The current selection
 * @returns Tick when every child is ticked, dash when only some are
 */
export function getRegionCheckState(region: AccessTreeRegion, selection: AccessTreeSelection): AccessCheckState {
  const states: AccessCheckState[] = [
    ...region.orgs.map(org => getOrgCheckState(org, selection)),
    ...region.orphans.map((site): AccessCheckState =>
      selection.dealerships.includes(site.id) ? 'checked' : 'unchecked'
    )
  ]

  // An empty region has nothing to reflect
  if (states.length === 0) {
    return 'unchecked'
  }

  // Every child ticked rolls up to a tick, none ticked rolls up to empty
  if (states.every(state => state === 'checked')) {
    return 'checked'
  }
  if (states.every(state => state === 'unchecked')) {
    return 'unchecked'
  }

  return 'partial'
}

/**
 * Whether a site's checkbox should read as ticked, either individually or
 * because its org is granted as a whole.
 *
 * @param dealership - The site to inspect
 * @param org - The org the site belongs to, or null for an orphan site
 * @param selection - The current selection
 * @returns True when the site is covered by the selection
 */
export function isDealershipChecked(
  dealership: AccessTreeDealership,
  org: AccessTreeOrg | null,
  selection: AccessTreeSelection
): boolean {
  // Sites inherit a tick from their org's scope grant
  if (org && isOrgScoped(org, selection)) {
    return true
  }

  return selection.dealerships.includes(dealership.id)
}

/**
 * Whether an org may be granted as a whole. A granter who only holds some of an
 * org's sites must not be able to widen that to the entire org.
 *
 * @param orgId - The org to test
 * @param scopableOrgIds - The orgs the granter holds outright, or null for all
 * @returns True when an org scope may be stored
 */
export function canScopeOrg(orgId: number, scopableOrgIds: ScopableOrgIds = null): boolean {
  return scopableOrgIds === null || scopableOrgIds.includes(orgId)
}

/**
 * Toggle an org branch. Where the granter holds the whole org this stores the
 * org as a scope, so its sites and any added later are covered. Otherwise it
 * falls back to ticking the org's visible sites one by one.
 *
 * @param org - The org branch that was clicked
 * @param selection - The current selection
 * @param scopableOrgIds - The orgs the granter holds outright, or null for all
 * @returns A new selection
 */
export function toggleOrg(
  org: AccessTreeOrg,
  selection: AccessTreeSelection,
  scopableOrgIds: ScopableOrgIds = null
): AccessTreeSelection {
  const siteIds = org.dealerships.map(site => site.id)
  const withoutOrgSites = selection.dealerships.filter(id => !siteIds.includes(id))

  // A ticked org clears back to nothing
  if (getOrgCheckState(org, selection) === 'checked') {
    return {
      orgs: selection.orgs.filter(id => id !== org.id),
      dealerships: withoutOrgSites
    }
  }

  // Without the whole org to give, the tick can only cover the sites on show
  if (!canScopeOrg(org.id, scopableOrgIds)) {
    return {
      orgs: selection.orgs.filter(id => id !== org.id),
      dealerships: [...new Set([...selection.dealerships, ...siteIds])]
    }
  }

  return {
    orgs: [...new Set([...selection.orgs, org.id])],
    dealerships: withoutOrgSites
  }
}

/**
 * Toggle a single site. Unticking a site covered by an org scope drops the org
 * grant and pins the org's remaining sites individually.
 *
 * @param dealership - The site that was clicked
 * @param org - The org the site belongs to, or null for an orphan site
 * @param selection - The current selection
 * @returns A new selection
 */
export function toggleDealership(
  dealership: AccessTreeDealership,
  org: AccessTreeOrg | null,
  selection: AccessTreeSelection
): AccessTreeSelection {
  // Carving a site out of an org scope has to expand the scope first
  if (org && isOrgScoped(org, selection)) {
    const remainingIds = org.dealerships.filter(site => site.id !== dealership.id).map(site => site.id)

    return {
      orgs: selection.orgs.filter(id => id !== org.id),
      dealerships: [...new Set([...selection.dealerships, ...remainingIds])]
    }
  }

  // Otherwise it is a plain individual grant
  if (selection.dealerships.includes(dealership.id)) {
    return {
      orgs: selection.orgs,
      dealerships: selection.dealerships.filter(id => id !== dealership.id)
    }
  }

  return {
    orgs: selection.orgs,
    dealerships: [...selection.dealerships, dealership.id]
  }
}

/**
 * Toggle a whole region. Selecting grants each org the granter holds outright as
 * a scope, so sites added to those orgs later are covered automatically. Orgs
 * the granter only partly holds fall back to their visible sites.
 *
 * @param region - The region branch that was clicked
 * @param selection - The current selection
 * @param scopableOrgIds - The orgs the granter holds outright, or null for all
 * @returns A new selection
 */
export function toggleRegion(
  region: AccessTreeRegion,
  selection: AccessTreeSelection,
  scopableOrgIds: ScopableOrgIds = null
): AccessTreeSelection {
  const orgIds = region.orgs.map(org => org.id)
  const orphanIds = region.orphans.map(site => site.id)
  const siteIds = region.orgs.flatMap(org => org.dealerships.map(site => site.id))

  // A fully ticked region clears everything beneath it
  if (getRegionCheckState(region, selection) === 'checked') {
    return {
      orgs: selection.orgs.filter(id => !orgIds.includes(id)),
      dealerships: selection.dealerships.filter(id => !siteIds.includes(id) && !orphanIds.includes(id))
    }
  }

  const scopableIds = orgIds.filter(orgId => canScopeOrg(orgId, scopableOrgIds))
  const siteOnlyIds = region.orgs
    .filter(org => !canScopeOrg(org.id, scopableOrgIds))
    .flatMap(org => org.dealerships.map(site => site.id))

  return {
    orgs: [...new Set([...selection.orgs, ...scopableIds])],
    dealerships: [
      ...new Set([
        ...selection.dealerships.filter(id => !siteIds.includes(id)),
        ...siteOnlyIds,
        ...orphanIds
      ])
    ]
  }
}

/**
 * Narrow the tree to nodes matching a search term. A region or org name match
 * keeps everything beneath it, a site match keeps only that site.
 *
 * @param tree - The full tree
 * @param query - The search term, matched case insensitively
 * @returns A pruned copy of the tree, or the tree itself when no term is given
 */
export function filterAccessTree(tree: AccessTreeRegion[], query: string): AccessTreeRegion[] {
  const term = (query ?? '').trim().toLowerCase()

  // No search term means no filtering
  if (!term) {
    return tree
  }

  const matches = (value: string | null): boolean => (value ?? '').toLowerCase().includes(term)

  return tree
    .map(region => {
      // A region match keeps the whole branch intact
      if (matches(region.name) || matches(region.id)) {
        return region
      }

      const orgs = region.orgs
        .map(org =>
          matches(org.name)
            ? org
            : {
                ...org,
                dealerships: org.dealerships.filter(site => matches(site.name) || matches(site.manufacturerName))
              }
        )
        .filter(org => org.dealerships.length > 0)

      return {
        ...region,
        orgs,
        orphans: region.orphans.filter(site => matches(site.name) || matches(site.manufacturerName))
      }
    })
    .filter(region => region.orgs.length > 0 || region.orphans.length > 0)
}

/**
 * Build the key identifying an org branch within a region, since the same org
 * can appear under more than one region.
 *
 * @param regionId - The region branch ID
 * @param orgId - The org ID
 * @returns A composite key
 */
export function accessTreeOrgKey(regionId: string, orgId: number): string {
  return `${regionId}:${orgId}`
}

/**
 * Index a tree so filtered branches can be resolved back to their unpruned
 * counterparts, keeping checkbox states based on the full site lists.
 *
 * @param tree - The full, unfiltered tree
 * @returns Lookups for regions by ID and orgs by region/org key
 */
export function indexAccessTree(tree: AccessTreeRegion[]): {
  regions: Map<string, AccessTreeRegion>
  orgs: Map<string, AccessTreeOrg>
} {
  const regions = new Map<string, AccessTreeRegion>()
  const orgs = new Map<string, AccessTreeOrg>()

  for (const region of tree) {
    regions.set(region.id, region)
    for (const org of region.orgs) {
      orgs.set(accessTreeOrgKey(region.id, org.id), org)
    }
  }

  return { regions, orgs }
}
